import React, { useRef } from 'react';
import { createModal } from 'react-modal-promise';
import { Trans, useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Label, FormGroup, Input, ButtonGroup } from 'reactstrap';

const components = { b: <strong />, br: <br />, i: <i />, a: <a /> };

function Footer({ buttons, cancel, submit, input }) {
    const { t } = useTranslation();
    return (
        Object.keys(buttons).length ?
            <ModalFooter>
                <ButtonGroup className="d-flex flex-grow-1">
                    {
                        Object.keys(buttons).map((button) =>
                        ({
                            'cancel':
                                <Button
                                    size="xl"
                                    color={buttons[button].color || 'secondary'}
                                    onClick={cancel}>{buttons[button].label || t('actions.cancel')}
                                </Button>
                            ,
                            'confirm':
                                <Button
                                    size="xl"
                                    color={buttons[button].color || 'primary'}
                                    onClick={submit}>{buttons[button].label || t('actions.accept')}
                                </Button>
                            ,
                        }[button]

                            ||

                            <Button
                                size="xl"
                                color={buttons[button].color || 'primary'}
                                onClick={() => submit(buttons[button]?.callback(input?.current?.value))}>
                                {buttons[button].label || t('actions.accept')}
                            </Button>
                        ))}
                </ButtonGroup>
            </ModalFooter>
            :
            <ModalFooter>
                <ButtonGroup className="d-flex flex-grow-1">
                    <Button size="xl" color="secondary" onClick={cancel}>{t('actions.cancel')}</Button>
                    <Button size="xl" color="primary" onClick={submit}>{t('actions.accept')}</Button>
                </ButtonGroup>
            </ModalFooter>
    )
}

function Header({ title, closeButton, cancel }) {
    return ((title || closeButton) &&
        <ModalHeader toggle={closeButton && cancel}>{title}</ModalHeader>
    )
}

function TranslatedLabel({ className, textT = '', translationParams = {} }) {
    return (
        <Label className={className}>
            <Trans
                i18nKey={textT}
                components={components}
                {...translationParams}
            >
                {translationParams.component}
            </Trans>
        </Label>
    )
}

const ConfirmModal = ({ isOpen, onResolve, text, title, buttons = {}, closeButton = false, size, textT, message, translationParams }) => {
    const submit = () => onResolve(true);
    const cancel = () => onResolve(null);
    return (
        <Modal isOpen={isOpen} centered size={size}>
            <Header title={title} closeButton={closeButton} cancel={cancel} />

            <ModalBody className="mt-3 text-center" hidden={(!text && !message)}>
                <Label htmlFor="name">{text || message}</Label>
            </ModalBody>

            <ModalBody className="mt-3 text-center" hidden={!textT}>
                <TranslatedLabel textT={textT} translationParams={translationParams} />
            </ModalBody>

            <Footer buttons={buttons} submit={submit} cancel={cancel} />
        </Modal>
    );
};

const AlertModal = ({ isOpen, onResolve, text, title, closeButton = false, size, textT, message, translationParams }) => {
    const { t } = useTranslation();
    const submit = () => onResolve(true);
    return (
        <Modal isOpen={isOpen} centered size={size}>
            <Header title={title} closeButton={closeButton} cancel={submit} />

            <ModalBody className="mt-3 text-center" hidden={(!text && !message)}>
                <Label htmlFor="name">{text || message}</Label>
            </ModalBody>

            <ModalBody className="mt-3 text-center" hidden={!textT}>
                <TranslatedLabel textT={textT} translationParams={translationParams} />
            </ModalBody>

            <ModalFooter>
                <ButtonGroup className="d-flex flex-grow-1">
                    <Button size="xl" color="secondary" onClick={submit}>{t('common.accept')}</Button>
                </ButtonGroup>
            </ModalFooter>
        </Modal>
    );
};

const PromptModal = ({ isOpen, onResolve, text, title, labelClassName, validateInput, buttons = {}, inputType = 'text', inputOptions = [], closeButton = false, size, textT = '', message = '', placeholder, translationParams }) => {
    const input = useRef();
    const { t } = useTranslation();
    const [hasError, setHasError] = React.useState()
    const submit = () => {
        if (validateInput) {
            const hasError = !input.current.value;
            setHasError(hasError)
            if (hasError) return;
        }
        onResolve(input.current.value || '')
    };
    const cancel = () => onResolve(null);

    console.log({inputOptions})

    return (
        <Modal isOpen={isOpen} centered size={size}>
            <Header title={title} closeButton={closeButton} cancel={cancel} />
            <ModalBody className="mt-3 text-center">
                <FormGroup>
                    {(text || message) && <Label cl className={labelClassName}  htmlFor="name">{text || message}</Label>}
                    <TranslatedLabel className={labelClassName}  textT={textT} translationParams={translationParams} />
                    <Input type={inputType} name="name" innerRef={input} placeholder={placeholder}>
                        {inputOptions.map(option => (
                            <option className="text-dark" value={option.value}>{option.label}</option>
                        ))}
                        
                    </Input>
                    <div className="text-left">
                        {hasError && <small className="text-danger text-left">{t('errors.Required')}</small>}
                    </div>
                </FormGroup>
            </ModalBody>
            <Footer buttons={buttons} submit={submit} cancel={cancel} input={input} />
        </Modal>
    );
};

const DialogModal = ({ isOpen, onResolve, text, title, buttons = {}, closeButton = false, size, message, textT, translationParams }) => {

    const submit = (value) => {
        onResolve(value)
    };
    const cancel = () => onResolve(null);

    return (
        <Modal isOpen={isOpen} centered size={size}>
            <Header title={title} closeButton={closeButton} cancel={cancel} />
            <ModalBody className="mt-3 text-center">
                <FormGroup>
                    {(text || message) && <Label htmlFor="name">{(text || message)}</Label>}
                    <TranslatedLabel textT={textT} translationParams={translationParams} />
                </FormGroup>
            </ModalBody>
            <Footer buttons={buttons} submit={submit} cancel={cancel} />
        </Modal>
    );
};

export const confirmModal = createModal(ConfirmModal, { exitTimeout: 0 });
export const alertModal = createModal(AlertModal, { exitTimeout: 0 });
export const promptModal = createModal(PromptModal, { exitTimeout: 0 });
export const dialogModal = createModal(DialogModal, { exitTimeout: 0 });

const Bootbox = {
    confirm(props) { return typeof props === 'string' ? confirmModal({ text: props }) : confirmModal(props) },
    alert(props) { return typeof props === 'string' ? alertModal({ text: props }) : alertModal(props) },
    prompt(props) { return typeof props === 'string' ? promptModal({ text: props }) : promptModal(props) },
    dialog(props) { return typeof props === 'string' ? dialogModal({ text: props }) : dialogModal(props) },
}

export default Bootbox;